/**
 * Finish Selector - Global Bronze/Silver/Gold variant preference
 * Allows users to select their preferred finish and:
 * - Auto-select that finish variant on product pages
 * - Update collection card prices based on selected finish
 * - Remember their preference across sessions
 */

import "../types/shopify.d";
import { getStorageString, setStorageString } from "../lib/storage";

type Finish = "default" | "bronze" | "silver" | "gold";

interface FinishKeywords {
  default: string[];
  bronze: string[];
  silver: string[];
  gold: string[];
}

class FinishSelector {
  private storageKey = "remerlinds_preferred_finish";
  public currentFinish: Finish;
  private finishKeywords: FinishKeywords;
  private formatter = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

  constructor() {
    this.currentFinish = this.getStoredFinish() || "default";
    this.finishKeywords = {
      default: [], // No keywords - doesn't affect variant selection
      bronze: ["bronze", "bronze finish"],
      silver: ["silver", "silver finish"],
      gold: ["gold", "gold finish"]
    };

    this.init();
  }

  private init(): void {
    // Initialize desktop finish selectors
    this.initDesktopSelectors();

    // Initialize mobile finish selectors
    this.initMobileSelectors();

    // Update UI to reflect current selection
    this.updateAllUIElements();

    // Only affect variants and prices if NOT the neutral "default" option
    if (!this.isDefaultFinish()) {
      // Pre-select finish variant on product pages
      if (this.isProductPage()) {
        this.schedulePreselectVariant();
      }

      // Update collection prices on page load
      this.updateCollectionPrices();
    }
  }

  /**
   * Schedule preselection with retry logic
   */
  private schedulePreselectVariant(): void {
    const tryPreselect = (attempt = 1, maxAttempts = 5): void => {
      const productInfo = document.querySelector<HTMLElement>("product-info, .product-info");

      if (
        productInfo &&
        productInfo.querySelectorAll('[name*="Finish"], [name*="Service Level"]').length > 0
      ) {
        this.preselectProductVariant();
      } else if (attempt < maxAttempts) {
        setTimeout(() => tryPreselect(attempt + 1, maxAttempts), 200 * attempt);
      }
    };

    requestAnimationFrame(() => {
      setTimeout(() => tryPreselect(), 300);
    });
  }

  /**
   * Get stored finish preference from localStorage
   */
  private getStoredFinish(): Finish | null {
    const stored = getStorageString(this.storageKey);
    if (stored && ["default", "bronze", "silver", "gold"].includes(stored)) {
      return stored as Finish;
    }
    return null;
  }

  /**
   * Check if current finish is the neutral "default" option
   * Default doesn't affect pricing or variant selection
   */
  private isDefaultFinish(): boolean {
    return this.currentFinish === "default";
  }

  /**
   * Store finish preference in localStorage
   */
  private setStoredFinish(finish: Finish): void {
    setStorageString(this.storageKey, finish);
  }

  /**
   * Initialize desktop dropdown selectors
   */
  private initDesktopSelectors(): void {
    // Disclosure button toggle
    const buttons = document.querySelectorAll<HTMLElement>("[data-finish-selector-button]");
    buttons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        const list = button.parentElement?.querySelector<HTMLElement>(
          "[data-finish-selector-list]"
        );
        if (!list) return;

        const isExpanded = button.getAttribute("aria-expanded") === "true";
        button.setAttribute("aria-expanded", String(!isExpanded));
        list.hidden = isExpanded;
      });
    });

    // Option selection
    const options = document.querySelectorAll<HTMLElement>("[data-finish-option]");
    options.forEach((option) => {
      option.addEventListener("click", (e) => {
        e.preventDefault();
        const finish = option.dataset.finishOption as Finish;
        if (finish) {
          this.selectFinish(finish);
        }

        // Close dropdown
        const list = option.closest<HTMLElement>("[data-finish-selector-list]");
        const button = option
          .closest(".disclosure")
          ?.querySelector<HTMLElement>("[data-finish-selector-button]");
        if (list && button) {
          list.hidden = true;
          button.setAttribute("aria-expanded", "false");
        }
      });
    });

    // Close dropdown when clicking outside
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".disclosure")) {
        document.querySelectorAll<HTMLElement>("[data-finish-selector-list]").forEach((list) => {
          list.hidden = true;
        });
        document
          .querySelectorAll<HTMLElement>("[data-finish-selector-button]")
          .forEach((button) => {
            button.setAttribute("aria-expanded", "false");
          });
      }
    });
  }

  /**
   * Initialize mobile finish selectors
   */
  private initMobileSelectors(): void {
    const mobileOptions = document.querySelectorAll<HTMLElement>(
      ".menu-drawer__finish-options [data-finish-option]"
    );
    mobileOptions.forEach((option) => {
      option.addEventListener("click", (e) => {
        e.preventDefault();
        const finish = option.dataset.finishOption as Finish;
        if (finish) {
          this.selectFinish(finish);
        }
      });
    });
  }

  /**
   * Select a finish and update everything
   */
  public selectFinish(finish: Finish): void {
    if (this.currentFinish === finish) return;

    this.currentFinish = finish;
    this.setStoredFinish(finish);
    this.updateAllUIElements();

    // Only affect variants and prices if NOT the neutral "default" option
    if (!this.isDefaultFinish()) {
      // Pre-select variant on product pages
      if (this.isProductPage()) {
        this.preselectProductVariant();
      }

      // Update collection card prices
      this.updateCollectionPrices();
    }

    // Dispatch custom event for other scripts to listen to
    document.dispatchEvent(
      new CustomEvent("finish:changed", {
        detail: { finish: this.currentFinish }
      })
    );
  }

  /**
   * Update all UI elements to reflect current selection
   */
  private updateAllUIElements(): void {
    const finish = this.currentFinish;

    // Update desktop dropdown current display
    document.querySelectorAll<HTMLElement>("[data-finish-selector-current]").forEach((current) => {
      const finishLabel = this.getFinishLabel(finish);
      const emoji = this.getFinishEmoji(finish);
      current.innerHTML = `
        <span class="finish-label finish-label--${finish}" data-finish-value="${finish}">
          ${emoji} ${finishLabel}
        </span>
      `;
    });

    // Update desktop dropdown active state
    document.querySelectorAll<HTMLElement>("[data-finish-option]").forEach((option) => {
      const optionFinish = option.dataset.finishOption;
      const isActive = optionFinish === finish;

      option.classList.toggle("disclosure__link--active", isActive);
      option.setAttribute("aria-current", String(isActive));
    });

    // Update mobile active state
    document
      .querySelectorAll<HTMLElement>(".menu-drawer__finish-options [data-finish-option]")
      .forEach((option) => {
        const optionFinish = option.dataset.finishOption;
        option.classList.toggle("active", optionFinish === finish);
      });
  }

  /**
   * Pre-select finish variant on product pages
   */
  private preselectProductVariant(): void {
    // Find the main product info container (avoid "Customers Also Bought" sections)
    const productInfo = document.querySelector<HTMLElement>(
      'product-info, .product-info, [data-section-type="product"]'
    );
    if (!productInfo) return;

    // Find finish options within the main product only
    const finishOptions = productInfo.querySelectorAll<HTMLInputElement>(
      '[name*="Finish"], [name*="Service Level"]'
    );
    if (finishOptions.length === 0) return;

    const keywords = this.finishKeywords[this.currentFinish];
    if (!keywords) return;

    finishOptions.forEach((option) => {
      const label = productInfo.querySelector<HTMLLabelElement>(`label[for="${option.id}"]`);
      if (label) {
        const labelText = label.textContent?.toLowerCase() || "";
        if (keywords.some((keyword) => labelText.includes(keyword))) {
          // Click the label to trigger Shopify's variant selection logic
          label.click();
        }
      }
    });
  }

  /**
   * Helper: Get finish label
   */
  private getFinishLabel(finish: Finish): string {
    const labels: Record<Finish, string> = {
      default: "Service Level",
      bronze: "Bronze",
      silver: "Silver",
      gold: "Gold"
    };
    return labels[finish] || "Service Level";
  }

  /**
   * Helper: Get finish emoji
   */
  private getFinishEmoji(finish: Finish): string {
    const emojis: Record<Finish, string> = {
      default: "✨",
      bronze: "🥉",
      silver: "🥈",
      gold: "🥇"
    };
    return emojis[finish] || "✨";
  }

  /**
   * Helper: Check if on product page
   */
  private isProductPage(): boolean {
    return (
      window.location.pathname.includes("/products/") ||
      !!document.querySelector("product-info") ||
      !!document.querySelector("[data-product-id]") ||
      document.body.classList.contains("template-product")
    );
  }

  /**
   * Update collection card prices based on selected finish
   * Reads variant data embedded in card-product.liquid and updates price display
   * Public so it can be called after AJAX cards are loaded
   */
  public updateCollectionPrices(): void {
    const variantDataScripts = document.querySelectorAll<HTMLScriptElement>(
      "script.product-variant-data"
    );

    variantDataScripts.forEach((script) => {
      try {
        const data = JSON.parse(script.textContent || "{}");
        const variants = data.variants;
        if (!variants || !Array.isArray(variants)) return;

        // Find variant matching current finish
        const keywords = this.finishKeywords[this.currentFinish];
        let matchingVariant = null;

        for (const variant of variants) {
          const title = (variant.title || variant.public_title || "").toLowerCase();
          if (keywords.some((kw) => title.includes(kw))) {
            matchingVariant = variant;
            break;
          }
        }

        // Fallback to first variant if no match
        if (!matchingVariant && variants.length > 0) {
          matchingVariant = variants[0];
        }

        if (!matchingVariant) return;

        // Find the card containing this variant data
        const card = script.closest(".card, .card-wrapper, .grid__item");
        if (!card) return;

        // Update price display
        this.updateCardPrice(card as HTMLElement, matchingVariant.price);
      } catch (e) {
        // Silent fail for invalid JSON
      }
    });
  }

  /**
   * Update a single card's price display
   * Handles both regular prices and discounted prices (with strikethrough + sale price)
   */
  private updateCardPrice(card: HTMLElement, priceInCents: number): void {
    const formatter = this.formatter;

    // Check if this card has a discount applied
    const discountContainer = card.querySelector<HTMLElement>("[data-discount-percentage]");
    const discountPercentage = discountContainer
      ? parseInt(discountContainer.dataset.discountPercentage || "0", 10)
      : 0;

    if (discountPercentage > 0) {
      // Card has discount - update both original (strikethrough) and sale prices
      const originalPrice = priceInCents / 100;
      const discountedPrice = Math.ceil(originalPrice * (1 - discountPercentage / 100) * 100) / 100;

      const formattedOriginal = formatter.format(originalPrice);
      const formattedSale = formatter.format(discountedPrice);

      // Update strikethrough original price (inside <s> tag or .yagi-original-price)
      const originalPriceEl = card.querySelector<HTMLElement>(
        "s.price-item--regular, .yagi-original-price"
      );
      if (originalPriceEl) {
        originalPriceEl.textContent = formattedOriginal;
      }

      // Update sale price
      const salePriceEl = card.querySelector<HTMLElement>(
        ".price-item--sale, .yagi-discounted-price"
      );
      if (salePriceEl) {
        salePriceEl.textContent = formattedSale;
      }
    } else {
      // No discount - update single price element
      const formattedPrice = formatter.format(priceInCents / 100);

      const priceSelectors = [
        ".price-item--regular:not(s .price-item--regular)",
        ".price-item",
        ".price span:not(.visually-hidden)"
      ];

      for (const selector of priceSelectors) {
        const priceEl = card.querySelector<HTMLElement>(selector);
        if (priceEl && !priceEl.classList.contains("visually-hidden")) {
          priceEl.textContent = formattedPrice;
          return;
        }
      }
    }
  }
}

// Early UI update from localStorage to prevent flash
(function initFinishSelectorEarly(): void {
  try {
    const stored = localStorage.getItem("remerlinds_preferred_finish");
    // Update UI if stored value exists (including the new "default" option)
    if (stored && ["default", "bronze", "silver", "gold"].includes(stored)) {
      const emojis: Record<string, string> = {
        default: "✨",
        bronze: "🥉",
        silver: "🥈",
        gold: "🥇"
      };
      const labels: Record<string, string> = {
        default: "Service Level",
        bronze: "Bronze",
        silver: "Silver",
        gold: "Gold"
      };
      const emoji = emojis[stored] || "✨";
      const label = labels[stored] || "Service Level";

      document.querySelectorAll<HTMLElement>("[data-finish-selector-current]").forEach((el) => {
        el.innerHTML = `<span class="finish-label finish-label--${stored}" data-finish-value="${stored}">${emoji} ${label}</span>`;
      });
    }
  } catch {
    // localStorage not available, continue with default
  }

  // Full initialization when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      (window as { finishSelector?: FinishSelector }).finishSelector = new FinishSelector();
    });
  } else {
    (window as { finishSelector?: FinishSelector }).finishSelector = new FinishSelector();
  }
})();

export { FinishSelector };
